/**
 * Line graph renderer: turns the computed layout into a static SVG (no scripts, no <style>),
 * so it renders the same in a README <img> as in the browser.
 */
import { GraphOptions } from '../options';
import { formatDate } from '../shared/dates';
import { escapeXml, formatNumber } from '../shared/escape';
import { lineStroke, paint, round, svgDocument } from '../shared/svg';
import { PreparedData } from '../shared/types';
import { layoutLine } from './layout';

const DASH: Record<GraphOptions['gridStyle'], string> = {
    dotted: ' stroke-dasharray="1.5 4.5" stroke-linecap="round"',
    dashed: ' stroke-dasharray="6 5"',
    solid: '',
};

const AREA_GRADIENT_ID = 'gag-area-fill';

export const renderLine = (data: PreparedData, options: GraphOptions): string => {
    const layout = layoutLine(data, options);
    const { colors, fontSize } = options;
    const { plot } = layout;
    const parts: string[] = [];
    const gridOpacity = round(options.gridOpacity / 100);
    const gridAttrs = `stroke="${paint(colors.grid)}" stroke-opacity="${gridOpacity}" stroke-width="1"`;

    if (layout.title) {
        parts.push(
            `<text x="${round(layout.title.x)}" y="${layout.title.y}" text-anchor="middle" font-size="${layout.title.size}" font-weight="600" fill="${paint(colors.text)}">${escapeXml(layout.title.text)}</text>`,
        );
    }
    if (layout.total) {
        parts.push(
            `<text x="${round(layout.total.x)}" y="${layout.total.y}" text-anchor="middle" font-size="${fontSize}" fill="${paint(colors.count)}">${escapeXml(layout.total.text)}</text>`,
        );
    }

    // Grid
    const grid: string[] = [];
    if (options.showGridY) {
        for (const tick of layout.yTicks.slice(1))
            grid.push(
                `<line x1="${round(plot.left)}" y1="${round(tick.y)}" x2="${round(plot.right)}" y2="${round(tick.y)}"${DASH[options.gridStyle]}/>`,
            );
    }
    if (options.showGridX) {
        for (const label of layout.xLabels)
            grid.push(
                `<line x1="${round(label.x)}" y1="${round(plot.top)}" x2="${round(label.x)}" y2="${round(plot.bottom)}"${DASH[options.gridStyle]}/>`,
            );
    }
    // Baseline is always drawn solid so the "0" axis stays readable.
    grid.push(
        `<line x1="${round(plot.left)}" y1="${round(plot.bottom)}" x2="${round(plot.right)}" y2="${round(plot.bottom)}"/>`,
    );
    parts.push(`<g ${gridAttrs}>${grid.join('')}</g>`);

    // Axis labels
    const labelFill = paint(colors.label);
    if (options.showYLabels) {
        const x = round(plot.left - Math.max(layout.pointRadius, 4) - fontSize * 0.5);
        parts.push(
            `<g font-size="${fontSize}" fill="${labelFill}" text-anchor="end">` +
                layout.yTicks
                    .map(
                        (tick) =>
                            `<text x="${x}" y="${round(tick.y)}" dy="0.35em">${formatNumber(tick.value)}</text>`,
                    )
                    .join('') +
                '</g>',
        );
    }
    if (options.showXLabels) {
        const y = round(plot.bottom + fontSize * 1.5);
        parts.push(
            `<g font-size="${fontSize}" fill="${labelFill}" text-anchor="middle">` +
                layout.xLabels
                    .map(
                        (label) =>
                            `<text x="${round(label.x)}" y="${y}">${escapeXml(label.text)}</text>`,
                    )
                    .join('') +
                '</g>',
        );
    }
    if (layout.yAxisTitle) {
        const { x, y } = layout.yAxisTitle;
        parts.push(
            `<text x="${round(x)}" y="${round(y)}" transform="rotate(-90 ${round(x)} ${round(y)})" text-anchor="middle" dy="0.35em" font-size="${fontSize}" fill="${labelFill}">Contributions</text>`,
        );
    }
    if (layout.xAxisTitle) {
        parts.push(
            `<text x="${round(layout.xAxisTitle.x)}" y="${round(layout.xAxisTitle.y)}" text-anchor="middle" font-size="${fontSize}" fill="${labelFill}">Date</text>`,
        );
    }

    // Area + line
    let defs = '';
    if (options.showArea && layout.areaPath && options.areaOpacity > 0) {
        const opacity = round(options.areaOpacity / 100);
        defs =
            `<linearGradient id="${AREA_GRADIENT_ID}" x1="0" y1="0" x2="0" y2="1">` +
            `<stop offset="0" stop-color="${paint(colors.area)}" stop-opacity="${opacity}"/>` +
            `<stop offset="1" stop-color="${paint(colors.area)}" stop-opacity="${round(opacity * 0.08)}"/>` +
            '</linearGradient>';
        parts.push(`<path d="${layout.areaPath}" fill="url(#${AREA_GRADIENT_ID})"/>`);
    }
    if (layout.linePath) {
        // On dense ranges (e.g. a year) a thick stroke merges neighbouring peaks.
        const n = layout.points.length;
        const spacing = n > 1 ? (plot.right - plot.left) / (n - 1) : Infinity;
        const lineWidth = round(Math.min(options.lineWidth, Math.max(1.5, spacing * 0.8)));
        const stroke = lineStroke(options, plot.left, plot.right);
        defs += stroke.defs;
        parts.push(
            `<path d="${layout.linePath}" fill="none" ${stroke.attrs} stroke-width="${lineWidth}" stroke-linejoin="round" stroke-linecap="round"/>`,
        );
    }

    // Points
    if (layout.pointRadius > 0) {
        const r = round(layout.pointRadius);
        const strokeWidth = Math.min(options.pointBorderWidth, layout.pointRadius);
        const stroke = strokeWidth
            ? ` stroke="${paint(colors.pointBorder)}" stroke-width="${round(strokeWidth)}"`
            : '';
        const circles = layout.points.map((point) => {
            const attrs = `cx="${round(point.x)}" cy="${round(point.y)}" r="${r}"`;
            if (!options.showTooltips) return `<circle ${attrs}/>`;
            const counted = `${point.count === 0 ? 'No' : formatNumber(point.count)} contribution${point.count === 1 ? '' : 's'}`;
            const label =
                layout.bucketing === 'day'
                    ? `${counted} on ${formatDate(point.date)}`
                    : `${escapeXml(point.title)}: ${counted}`;
            return `<circle ${attrs}><title>${label}</title></circle>`;
        });
        parts.push(`<g fill="${paint(colors.point)}"${stroke}>${circles.join('')}</g>`);
    }

    // Values above points
    if (layout.valueIndices.length) {
        const offset = Math.max(layout.pointRadius, options.lineWidth / 2) + fontSize * 0.6;
        parts.push(
            `<g font-size="${Math.max(8, fontSize - 1)}" font-weight="600" fill="${paint(colors.count)}" text-anchor="middle">` +
                layout.valueIndices
                    .map((i) => {
                        const p = layout.points[i];
                        return `<text x="${round(p.x)}" y="${round(p.y - offset)}">${formatNumber(p.count)}</text>`;
                    })
                    .join('') +
                '</g>',
        );
    }

    return svgDocument({
        width: layout.width,
        height: layout.height,
        options,
        title: layout.title?.text ?? `${data.username}'s Contribution Graph`,
        description: data.totalText,
        defs,
        body: parts.join(''),
    });
};
